// Write-side type hygiene for contact documents.
//
// WHY THIS EXISTS (2026-08-19 incident): the file import (ImportContactsFromFile)
// wrote every spreadsheet cell as a raw string — empty cells as "" — and the same
// disease exists in older data from other writers. Measured on prod: 1,064 users /
// ~2.5M contacts carried string-typed numeric fields. Two customer-facing failures:
// - floorsMin/floorsMax "" seeded a forced 0-0 floor filter in the Matches tab
//   (getClosestValidFloor coerced "" to 0);
// - string roomsMin/roomsMax failed the listings-search endpoint's zod z.number()
//   → silent 400 → zero matches until the realtor re-typed the value. A ""
//   customerBudget also excludes the contact from server-side listing alerts
//   (treated as budget=0).
// The stored data was repaired by webot-server adHocOperations
// (fix-string-typed-contact-fields-platform.ts); these helpers keep it clean.

import type firebase from "firebase/compat";

/** Every numeric field on RealEstateContact (keep in sync with sharedSchema). */
export const NUMERIC_CONTACT_FIELDS = [
  "roomsMin",
  "roomsMax",
  "floorsMin",
  "floorsMax",
  "customerBudget",
  "customerBudgetMin",
  "customerBudgetRent",
  "customerBudgetRentMin",
  "squareMeterMin",
  "squareMeterMax",
  "plotSizeMin",
  "plotSizeMax",
  "parkingSpacesCount",
  "windDirectionsCount"
] as const;

/**
 * Coerces string values in the numeric contact fields, in place, on a write payload:
 * - ""/whitespace-only (and literal "null"/"undefined") → `deleteSentinel`
 *   (FieldValue.delete() — clears a previously poisoned stored value on merge writes;
 *   a no-op for brand-new docs).
 * - numeric string (commas/whitespace/₪ tolerated) → the number.
 * - anything else (free text like "עד 2 מיליון") → the key is DROPPED: a junk string
 *   in a numeric field silently breaks listing alerts for the contact (NaN budget
 *   never matches), which is worse than not storing it.
 *
 * Non-string values (numbers, null, undefined) pass through untouched, so callers
 * that legitimately write null (EditContactDialog) are unaffected.
 */
export function sanitizeContactNumericFields(
  data: Record<string, unknown>,
  deleteSentinel: firebase.firestore.FieldValue
): void {
  for (const field of NUMERIC_CONTACT_FIELDS) {
    const value = data[field];
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (trimmed === "" || trimmed === "null" || trimmed === "undefined") {
      data[field] = deleteSentinel;
      continue;
    }
    const cleaned = trimmed.replace(/[,\s₪]/g, "");
    const parsed = Number(cleaned);
    if (cleaned !== "" && Number.isFinite(parsed)) {
      data[field] = parsed;
    } else {
      console.warn(`sanitizeContactNumericFields: dropping non-numeric ${field} value`, value);
      delete data[field];
    }
  }
}

/**
 * Import-only: removes every key whose value is an empty/whitespace-only string.
 * In a spreadsheet an empty cell means "no value", never "clear the field" — writing
 * it as "" is what poisoned 9,919 contacts on 2026-08-19. NOT for dialog saves,
 * where "" on a text field is a legitimate clear.
 */
export function dropEmptyStringFields<T extends Record<string, unknown>>(data: T): Partial<T> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === "string" && value.trim() === "") continue;
    result[key] = value;
  }
  return result as Partial<T>;
}
